import React from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { APP } from "@/constants/testIds";
import {
  LayoutDashboard, Sparkles, ClipboardList, LineChart, FileSpreadsheet, ShieldCheck,
  Target, Compass, Presentation, DollarSign, HandCoins, Layers, Building2, Trophy,
  Gauge, LogOut, ArrowUpRight, TrendingUp, Users2, Globe, CreditCard, ScrollText, UserCog, Lock,
} from "lucide-react";

const ENTREPRENEUR_NAV = [
  { to: "/app", end: true, icon: LayoutDashboard, label: "Dashboard", tid: APP.navDashboard },
  { to: "/app/onboarding", icon: ClipboardList, label: "Business DNA™", tid: APP.navOnboarding },
  { to: "/app/health-score", icon: LineChart, label: "Health Score", tid: APP.navHealth },
  { to: "/app/predictor", icon: TrendingUp, label: "Success Predictor™", tid: "app-nav-predictor" },
  { to: "/app/einstein", icon: Sparkles, label: "Business Einstein™", tid: APP.navEinstein },
];
const AGENT_NAV = [
  { to: "/app/business-plan", icon: FileSpreadsheet, label: "Business Plan", tid: APP.navPlan },
  { to: "/app/auditor", icon: ShieldCheck, label: "Business Auditor", tid: APP.navAuditor },
  { to: "/app/marketing-plan", icon: Target, label: "Marketing Plan", tid: APP.navMarketing },
  { to: "/app/industry", icon: Compass, label: "Industry Intelligence", tid: APP.navIndustry },
  { to: "/app/pitch", icon: Presentation, label: "Pitch Outliner", tid: APP.navPitch },
  { to: "/app/financials", icon: DollarSign, label: "Financials", tid: APP.navFinancials },
  { to: "/app/capital", icon: HandCoins, label: "Capital Readiness", tid: APP.navCapital },
  { to: "/app/assets", icon: Layers, label: "Assets Library", tid: APP.navAssets },
];

export default function AppShell() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  if (!user) return null;

  const isOrgAdmin = ["org_owner", "org_admin", "program_director", "coach"].includes(user.role);
  const isPlatformAdmin = ["super_admin", "platform_admin"].includes(user.role);
  const isJudge = ["judge", "org_owner", "org_admin", "program_director"].includes(user.role);

  return (
    <div className="grid min-h-screen grid-cols-[260px_1fr] bg-stone-50">
      <aside data-testid={APP.sidebar} className="flex flex-col border-r border-stone-200 bg-white">
        <Link to="/app" className="flex items-center gap-2 border-b border-stone-200 px-5 py-4">
          <div className="flex h-8 w-8 items-center justify-center rounded-md bg-slate-950 text-white">
            <span className="font-display text-sm font-bold">A</span>
          </div>
          <div className="leading-tight">
            <div className="font-display text-base font-bold text-slate-950">ATLAS™</div>
            <div className="label-mono !text-[9px] !tracking-wider">The IXL Business Pro</div>
          </div>
        </Link>
        <nav className="flex-1 overflow-y-auto px-3 py-4">
          <NavSection title="Entrepreneur">
            {ENTREPRENEUR_NAV.map((n) => <NavItem key={n.to} {...n} />)}
          </NavSection>
          <NavSection title="AI Modules">
            {AGENT_NAV.map((n) => <NavItem key={n.to} {...n} />)}
          </NavSection>
          {isOrgAdmin && (
            <NavSection title="Organization">
              <NavItem to="/app/org-admin" icon={Building2} label="Org Dashboard" tid={APP.navOrgAdmin} />
              <NavItem to="/app/program-director" icon={Users2} label="Program Director™" tid="app-nav-program-director" />
              <NavItem to="/app/economic-impact" icon={Globe} label="Economic Impact™" tid="app-nav-economic-impact" />
            </NavSection>
          )}
          <NavSection title="Account">
            <NavItem to="/app/billing" icon={CreditCard} label="Billing" tid="app-nav-billing" />
            <NavItem to="/app/security" icon={Lock} label="Security & 2FA" tid="app-nav-security" />
          </NavSection>
          {isJudge && (
            <NavSection title="Competitions">
              <NavItem to="/app/judge" icon={Trophy} label="Judge Dashboard" tid={APP.navJudge} />
            </NavSection>
          )}
          {isPlatformAdmin && (
            <NavSection title="Platform">
              <NavItem to="/app/platform" icon={Gauge} label="Platform Analytics" tid={APP.navPlatform} />
              <NavItem to="/app/platform/users" icon={UserCog} label="User Management" tid="app-nav-platform-users" />
              <NavItem to="/app/platform/audit" icon={ScrollText} label="Audit Log" tid="app-nav-platform-audit" />
            </NavSection>
          )}
        </nav>
        <div className="border-t border-stone-200 p-3">
          <div className="mb-2 px-2">
            <div className="text-sm font-medium text-slate-950 truncate">{user.full_name}</div>
            <div className="label-mono !text-[9px]">{user.role.replace(/_/g, " ")}</div>
          </div>
          <button data-testid={APP.logout} onClick={logout} className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm text-stone-600 transition-colors hover:bg-stone-100 hover:text-slate-950">
            <LogOut className="h-4 w-4" />
            Sign out
          </button>
        </div>
      </aside>
      <main className="min-h-screen">
        <div className="border-b border-stone-200 bg-white/80 backdrop-blur-xl">
          <div className="flex items-center justify-between px-8 py-4">
            <div className="label-mono">Workspace</div>
            <div className="flex items-center gap-2">
              <Link to="/" className="btn-ghost !text-xs">
                Marketing Site <ArrowUpRight className="h-3 w-3" />
              </Link>
            </div>
          </div>
        </div>
        <div className="px-8 py-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
}

function NavSection({ title, children }) {
  return (
    <div className="mb-6">
      <div className="label-mono !text-[9px] px-3 mb-2">{title}</div>
      <div className="space-y-0.5">{children}</div>
    </div>
  );
}

function NavItem({ to, end, icon: Icon, label, tid }) {
  return (
    <NavLink
      to={to}
      end={end}
      data-testid={tid}
      className={({ isActive }) => `flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors ${isActive ? "bg-slate-950 text-white" : "text-stone-600 hover:bg-stone-100 hover:text-slate-950"}`}
    >
      <Icon className="h-4 w-4" strokeWidth={1.5} />
      <span>{label}</span>
    </NavLink>
  );
}
